<template>
  <nav
    class="shadow-none navbar navbar-main navbar-expand-lg border-radius-xl px-0"
    v-bind="$attrs"
    id="navbarBlur"
    data-scroll="true"
    :class="isAbsolute ? 'mt-4' : 'mt-0'"
  >
    <div class="px-0 py-1 d-flex justify-content-between w-100 navbar-field">
      <breadcrumbs
        :currentPage="currentRouteName"
        :color="color"
        class="navbar-breadcrumb"
      />

      <div class="mt-2 mt-sm-0 align-content-center navbar-actions" id="navbar">
        <ul class="navbar-nav justify-content-end gap-4">
          <li
            class="nav-item align-items-center"
            v-if="hasToShow"
            :style="{ display: isSidebarOpened ? 'flex' : 'none' }"
          >
            <button @click="toggleSidebar" class="logout-button" ref="navbar">
              <i class="material-icons"> menu </i>
            </button>
          </li>

          <!-- Plan Badge -->
          <li class="nav-item d-none d-md-flex align-items-center" v-if="isPlanActive">
            <div 
              class="plan-badge d-flex align-items-center" 
              :class="{ 'plan-diamond': isLifetime, 'plan-starter': !isLifetime }"
            >
              <div class="plan-icon">
                <i class="fas fa-crown"></i>
              </div>
              <div class="plan-info d-flex flex-column">
                <span class="plan-name">{{ planLabel }}</span>
                <span class="plan-status" v-if="isLifetime">Usuário Vitalício</span>
                <span class="plan-status" v-else>Assinante até: {{ planExpiration }}</span>
              </div>
            </div>
          </li>

          <li class="nav-item d-flex align-items-center gap-1 theme-toggles">
            <button
              type="button"
              class="theme-toggle-btn"
              :class="{ 'is-active': isDarkMode }"
              :title="isDarkMode ? 'Tema escuro' : 'Tema claro'"
              :aria-label="isDarkMode ? 'Ativar tema claro' : 'Ativar tema escuro'"
              @click="toggleDarkMode"
            >
              <i class="material-icons">{{ isDarkMode ? "dark_mode" : "light_mode" }}</i>
            </button>
            <button
              v-if="isDarkMode"
              type="button"
              class="theme-toggle-btn"
              :class="{ 'is-active': sidebarLight }"
              :title="sidebarLight ? 'Lateral clara' : 'Lateral escura'"
              :aria-label="sidebarLight ? 'Usar lateral escura' : 'Usar lateral clara'"
              @click="toggleSidebarLight"
            >
              <i class="material-icons">{{ sidebarLight ? "view_sidebar" : "vertical_split" }}</i>
            </button>
          </li>

          <li
            class="nav-item d-flex align-items-center"
            v-if="user && (user.role === 'USER' || user.role === 'ADMIN')"
          >
            <button
              type="button"
              class="logout-button ann-bell"
              :class="{ 'ann-bell--unread': unreadCount > 0 }"
              @click="toggleAnnouncements"
              aria-label="Novidades"
            >
              <i class="material-icons">{{
                unreadCount > 0 ? "notifications_active" : "notifications"
              }}</i>
              <span v-if="unreadCount > 0" class="ann-badge">{{
                unreadCount > 9 ? "9+" : unreadCount
              }}</span>
            </button>
          </li>

          <li class="nav-item d-flex align-items-center">
            <button @click="logout" class="logout-button" ref="logoutButton">
              <i class="material-icons"> logout </i>
            </button>
          </li>
        </ul>
      </div>
    </div>

    <div
      v-if="annOpen"
      class="ann-drawer-backdrop"
      @click.self="annOpen = false"
    >
      <aside class="ann-drawer" role="dialog" aria-label="Novidades">
        <div class="ann-drawer-header">
          <div>
            <h6 class="mb-0">Novidades</h6>
            <small class="text-secondary">Atualizações da plataforma</small>
          </div>
          <div class="d-flex gap-2 align-items-center">
            <button
              v-if="unreadCount > 0"
              type="button"
              class="btn btn-sm btn-link mb-0"
              @click="markAllRead"
            >
              Marcar todas
            </button>
            <button type="button" class="logout-button" @click="annOpen = false">
              <i class="material-icons">close</i>
            </button>
          </div>
        </div>
        <div class="ann-drawer-body">
          <div v-if="annLoading" class="text-secondary text-sm p-3">Carregando...</div>
          <div v-else-if="announcements.length === 0" class="text-secondary text-sm p-3">
            Nenhuma novidade no momento.
          </div>
          <article
            v-for="item in announcements"
            :key="item.id"
            class="ann-item"
            :class="{ 'ann-item--unread': !item.is_read }"
            @click="openAnnouncement(item)"
          >
            <div class="ann-item-top">
              <strong>{{ item.title }}</strong>
              <span v-if="!item.is_read" class="ann-dot"></span>
            </div>
            <p class="ann-item-body mb-1">{{ item.body }}</p>
            <small class="text-secondary">{{ formatAnnDate(item.published_at) }}</small>
            <a
              v-if="item.link_url"
              class="ann-item-link"
              :href="item.link_url"
              target="_blank"
              rel="noopener noreferrer"
              @click.stop
            >
              Abrir link
            </a>
          </article>
        </div>
      </aside>
    </div>
  </nav>
</template>
<script>
import AuthHttp from "@/http/AuthHttp.js";
import AnnouncementsHttp from "@/http/AnnouncementsHttp.js";
import LogService from "@/services/LogService.js";
import Breadcrumbs from "../Breadcrumbs.vue";
import { mapMutations, mapState } from "vuex";

export default {
  name: "navbar",
  components: {
    Breadcrumbs,
  },
  data() {
    return {
      showMenu: false,
      windowWidth: window.innerWidth,
      annOpen: false,
      annLoading: false,
      announcements: [],
      unreadCount: 0,
      unreadTimer: null,
    };
  },
  props: ["minNav", "color"],
  created() {
    this.minNav;
  },
  mounted() {
    document.addEventListener("click", this.handleClickOutside);
    window.addEventListener("resize", this.updateWidth);
    this.refreshUnread();
    this.unreadTimer = setInterval(this.refreshUnread, 60000);
  },
  beforeUnmount() {
    document.removeEventListener("click", this.handleClickOutside);
    window.removeEventListener("resize", this.updateWidth);
    if (this.unreadTimer) clearInterval(this.unreadTimer);
  },
  computed: {
    ...mapState(["isRTL", "isAbsolute", "isPinned", "isDarkMode", "sidebarLight"]),

    currentRouteName() {
      return this.$route.name;
    },
    isSidebarOpened() {
      return this.isPinned;
    },
    hasToShow() {
      return this.windowWidth <= 1000;
    },
    userName() {
      return this.user?.full_name || "";
    },
    user() {
      return this.$store.getters.user;
    },
    planLabel() {
      if (!this.user?.use_plan) return null;
      if (this.user.plan_active === "VITALICIO") return "Diamond";
      if (this.user.plan_active === "ANUAL") return "Anual";
      return "Starter";
    },
    isLifetime() {
      return this.user?.plan_active === "VITALICIO";
    },
    planExpiration() {
      if (!this.user?.plan_expiration) return null;
      const date = new Date(this.user.plan_expiration);
      return date.toLocaleDateString("pt-BR");
    },
    isPlanActive() {
      const u = this.user;
      if (!u || !u.use_plan) return false;

      if (u.plan_active === 'VITALICIO') return true;

      if ((u.plan_active === 'MENSAL' || u.plan_active === 'ANUAL') && u.plan_expiration) {
        const [year, month, day] = u.plan_expiration.split('-').map(Number);
        const expDate = new Date(year, month - 1, day, 23, 59, 59);
        return expDate >= new Date();
      }
      return false;
    },
  },
  methods: {
    ...mapMutations([
      "navbarMinimize",
      "toggleConfigurator",
      "toggleDarkMode",
      "toggleSidebarLight",
    ]),

    updateWidth() {
      this.windowWidth = window.innerWidth;
    },

    toggleSidebar() {
      this.navbarMinimize();
    },

    formatAnnDate(value) {
      if (!value) return "";
      const d = new Date(String(value).replace(" ", "T"));
      if (Number.isNaN(d.getTime())) return value;
      return d.toLocaleString("pt-BR");
    },

    async refreshUnread() {
      if (!this.user || (this.user.role !== "USER" && this.user.role !== "ADMIN")) {
        this.unreadCount = 0;
        return;
      }
      try {
        const res = await AnnouncementsHttp.unreadCount();
        this.unreadCount = Number(res.data?.unread || 0);
      } catch (e) {
        // fail-open
      }
    },

    async toggleAnnouncements() {
      this.annOpen = !this.annOpen;
      if (this.annOpen) {
        await this.loadAnnouncements();
      }
    },

    async loadAnnouncements() {
      try {
        this.annLoading = true;
        const res = await AnnouncementsHttp.list();
        this.announcements = Array.isArray(res.data?.data)
          ? res.data.data
          : Array.isArray(res.data)
          ? res.data
          : [];
        await this.refreshUnread();
      } catch (e) {
        console.error(e);
      } finally {
        this.annLoading = false;
      }
    },

    async openAnnouncement(item) {
      if (!item.is_read) {
        try {
          await AnnouncementsHttp.markRead(item.id);
          item.is_read = true;
          await this.refreshUnread();
        } catch (e) {
          console.error(e);
        }
      }
    },

    async markAllRead() {
      try {
        await AnnouncementsHttp.markAllRead();
        this.announcements = this.announcements.map((a) => ({
          ...a,
          is_read: true,
        }));
        this.unreadCount = 0;
      } catch (e) {
        console.error(e);
      }
    },

    async logout() {
      try {
        const user = this.$store.getters["user"];
        await LogService.logLogout(user?.email || "");
        await AuthHttp.logout();
      } catch (error) {
        console.error("Erro ao realizar logout no servidor:", error);
      } finally {
        this.$store.commit("logout");
      }
    },

    /**
     * Manipula cliques fora da barra de navegação para fechar menus
     */
    handleClickOutside(event) {
      if (!this.isPinned) {
        if (
          !this.$refs.navbar?.contains(event.target) &&
          !this.$refs.logoutButton?.contains(event.target)
        ) {
          this.navbarMinimize();
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
.logout-button {
  border: none;
  background-color: transparent;
  margin: 0;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  color: black;

  &:hover {
    color: #7b23d3;
    transform: scale(1.2);
  }

  &:active {
    transform: scale(0.95);
  }
}

.navbar-field {
  height: 50px;
}

.navbar-breadcrumb {
  display: flex;
  align-items: center;
}

.navbar-actions {
  background-color: white;
  padding-inline: 14px;
  border-radius: 1000px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.12);
  display: flex;
  align-items: center;
  min-height: 44px;
}

.plan-badge {
  padding: 6px 16px;
  border-radius: 12px;
  gap: 12px;
  transition: all 0.3s ease;
  user-select: none;
}

.plan-starter {
  background: linear-gradient(135deg, #FFF9E1 0%, #FFE5A3 100%);
  border: 1px solid rgba(255, 166, 0, 0.3);
  box-shadow: 0 4px 12px rgba(255, 191, 0, 0.2);
  
  .plan-icon {
    color: #E67E22;
    filter: drop-shadow(0 2px 4px rgba(0,0,0,0.1));
    animation: pulse-small 2s infinite;
  }
  
  .plan-name {
    color: #A04000;
    font-weight: 900;
    font-size: 11px;
    letter-spacing: 1.2px;
    text-transform: uppercase;
  }
  
  .plan-status {
    color: #6a1b9a;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.2px;
  }
}

.plan-diamond {
  background: linear-gradient(135deg, #1a1a1a 0%, #333333 100%);
  border: 1px solid rgba(255, 215, 0, 0.3);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  
  .plan-icon {
    color: #ffd700;
    filter: drop-shadow(0 0 5px rgba(255, 215, 0, 0.5));
  }
  
  .plan-name {
    color: #ffd700;
    font-weight: 900;
    font-size: 12px;
    letter-spacing: 1px;
    text-transform: uppercase;
  }
  
  .plan-status {
    color: white;
    font-size: 10px;
    font-weight: 600;
    opacity: 0.9;
  }
}

.plan-icon {
  font-size: 18px;
  display: flex;
  align-items: center;
}

.plan-info {
  line-height: 1.2;
}

@keyframes pulse-small {
  0% { transform: scale(1); opacity: 1; }
  50% { transform: scale(1.1); opacity: 0.85; }
  100% { transform: scale(1); opacity: 1; }
}

.ann-bell {
  position: relative;
}

.ann-bell--unread .material-icons {
  color: #e41b80;
  animation: pulse-small 1.6s ease-in-out infinite;
}

.ann-badge {
  position: absolute;
  top: -2px;
  right: -2px;
  min-width: 16px;
  height: 16px;
  padding: 0 4px;
  border-radius: 999px;
  background: #e41b80;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  line-height: 16px;
  text-align: center;
}

.ann-drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.35);
  z-index: 1200;
}

.ann-drawer {
  position: absolute;
  top: 0;
  right: 0;
  width: min(380px, 100%);
  height: 100%;
  background: #fff;
  box-shadow: -12px 0 40px rgba(15, 23, 42, 0.18);
  display: flex;
  flex-direction: column;
}

.ann-drawer-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 0.75rem;
  padding: 1rem 1.1rem;
  border-bottom: 1px solid #e9ecef;
}

.ann-drawer-body {
  overflow: auto;
  padding: 0.75rem;
}

.ann-item {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 0.85rem 0.9rem;
  margin-bottom: 0.65rem;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s ease, background 0.15s ease;
}

.ann-item:hover {
  border-color: rgba(228, 27, 128, 0.35);
}

.ann-item--unread {
  background: #fff7fb;
  border-color: rgba(228, 27, 128, 0.28);
}

.ann-item-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  margin-bottom: 0.35rem;
}

.ann-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e41b80;
  flex-shrink: 0;
}

.ann-item-body {
  color: #67748e;
  font-size: 0.86rem;
  line-height: 1.4;
  white-space: pre-wrap;
}

.ann-item-link {
  display: inline-block;
  margin-top: 0.45rem;
  font-size: 0.82rem;
  font-weight: 650;
  color: #661cb0;
}

.theme-toggles {
  gap: 0.25rem !important;
}

.theme-toggle-btn {
  border: 1px solid rgba(94, 41, 176, 0.18);
  background: #fff;
  color: #5e29b0;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease,
    box-shadow 0.15s ease;
}

.theme-toggle-btn .material-icons {
  font-size: 1.15rem;
}

.theme-toggle-btn:hover {
  background: #f3eefb;
  border-color: rgba(94, 41, 176, 0.35);
}

.theme-toggle-btn.is-active {
  background: #5e29b0;
  border-color: #5e29b0;
  color: #fff;
  box-shadow: 0 4px 12px rgba(94, 41, 176, 0.28);
}
</style>

<style>
body.dark-version .navbar-actions {
  background: #202940;
  box-shadow: 0 4px 18px rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

body.dark-version .theme-toggle-btn {
  background: transparent;
  border-color: rgba(255, 255, 255, 0.16);
  color: rgba(255, 255, 255, 0.9);
}

body.dark-version .theme-toggle-btn:hover {
  background: rgba(255, 255, 255, 0.08);
}

body.dark-version .theme-toggle-btn.is-active {
  background: #7c3aed;
  border-color: #7c3aed;
  color: #fff;
}

body.dark-version .ann-drawer {
  background: #202940;
  box-shadow: -12px 0 40px rgba(0, 0, 0, 0.45);
}

body.dark-version .ann-drawer-header {
  border-bottom-color: rgba(255, 255, 255, 0.12);
}

body.dark-version .ann-drawer-header h6,
body.dark-version .ann-item-top strong {
  color: rgba(255, 255, 255, 0.92);
}

body.dark-version .ann-item {
  background: #1a2035;
  border-color: rgba(255, 255, 255, 0.12);
}

body.dark-version .ann-item--unread {
  background: rgba(124, 58, 237, 0.18);
  border-color: rgba(124, 58, 237, 0.4);
}

body.dark-version .ann-item-body {
  color: rgba(255, 255, 255, 0.65);
}

body.dark-version .logout-button {
  color: rgba(255, 255, 255, 0.85);
}
</style>
